import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { printPage } from "../../lib/print";

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const STD_START = "08:30";
const STD_END = "14:00";

const CHANGE_LABELS = {
  weekly_schedule: { label: "Weekly Schedule", cls: "bg-blue-100 text-blue-800" },
  alt_plan_added: { label: "Plan Added", cls: "bg-emerald-100 text-emerald-800" },
  alt_plan_updated: { label: "Plan Updated", cls: "bg-amber-100 text-amber-800" },
  alt_plan_ended: { label: "Plan Ended", cls: "bg-rose-100 text-rose-800" },
  initial: { label: "Initial", cls: "bg-slate-100 text-slate-700" },
};

const STATUS_LABELS = {
  upcoming: { label: "Upcoming", cls: "bg-indigo-100 text-indigo-800" },
  active: { label: "Active", cls: "bg-green-100 text-green-800" },
  superseded: { label: "Superseded", cls: "bg-gray-100 text-gray-600" },
  ended: { label: "Ended", cls: "bg-rose-100 text-rose-800" },
  cancelled: { label: "Cancelled", cls: "bg-gray-100 text-gray-500 line-through" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function dayLabel(cfg) {
  if (!cfg || typeof cfg !== "object") return "—";
  if (cfg.expected === false) return "Not expected";
  return `${cfg.start || STD_START}–${cfg.end || STD_END}`;
}

// School year of an effective date (academic year Aug 1 -> Jul 31).
export function schoolYearOf(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const startY = d.getMonth() >= 7 ? y : y - 1; // Aug (7) onward is the new year
  return `${startY}–${startY + 1}`;
}

/** Mini weekday cards for a single weekly schedule snapshot. */
export function WeeklyDayCards({ schedule, highlightDays = [] }) {
  const sched = schedule || {};
  return (
    <div className="grid grid-cols-5 gap-1.5">
      {WEEKDAYS.map((d) => {
        const hl = highlightDays.includes(d);
        return (
          <div key={d} className={`text-center p-1.5 rounded border text-[11px] ${hl ? "border-amber-400 bg-amber-50" : "border-gray-200"}`}>
            <div className="font-medium capitalize">{d.slice(0, 3)}</div>
            <div className="text-muted-foreground">{dayLabel(sched[d])}</div>
          </div>
        );
      })}
    </div>
  );
}

/** Before -> after weekly comparison. */
export function BeforeAfterWeekly({ before, after }) {
  const changed = WEEKDAYS.filter((d) => dayLabel((before || {})[d]) !== dayLabel((after || {})[d]));
  return (
    <div className="space-y-2">
      <div>
        <div className="text-[11px] font-semibold text-gray-500 mb-1">Before</div>
        <WeeklyDayCards schedule={before} highlightDays={changed} />
      </div>
      <div className="flex justify-center text-gray-400 text-xs">↓</div>
      <div>
        <div className="text-[11px] font-semibold text-[#00305E] mb-1">After</div>
        <WeeklyDayCards schedule={after} highlightDays={changed} />
      </div>
    </div>
  );
}

function PlanSnapshotView({ snap, title }) {
  if (!snap) return <div className="text-xs text-muted-foreground">{title}: —</div>;
  const when = snap.full_day_out
    ? "Full day out"
    : snap.plan_type === "range"
      ? `${snap.start_date || "?"} – ${snap.end_date || "?"}`
      : snap.plan_type === "dates"
        ? (snap.dates || []).join(", ")
        : (snap.days || []).map((x) => x.slice(0, 3)).join(", ");
  const times = snap.full_day_out ? "" : [snap.arrival_time, snap.departure_time].filter(Boolean).join("–");
  return (
    <div className="text-xs text-gray-700">
      <span className="font-semibold text-gray-500">{title}: </span>
      {snap.destination || "Plan"} · {when}{times ? ` · ${times}` : ""}
    </div>
  );
}

export function ChangeDetail({ change }) {
  const isWeekly = change.change_type === "weekly_schedule" || change.change_type === "initial";
  if (isWeekly) {
    if (change.change_type === "initial" || !change.before) {
      return (
        <div>
          <div className="text-[11px] font-semibold text-[#00305E] mb-1">Schedule</div>
          <WeeklyDayCards schedule={change.after || change.before} />
        </div>
      );
    }
    return <BeforeAfterWeekly before={change.before} after={change.after} />;
  }
  // alternate plan
  return (
    <div className="space-y-1">
      {change.before && <PlanSnapshotView snap={change.before} title="Before" />}
      {change.after && <PlanSnapshotView snap={change.after} title="After" />}
    </div>
  );
}

function buildPrintHtml(studentName, rows, yearFilter) {
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const body = rows.map((c) => {
    const type = (CHANGE_LABELS[c.change_type] || {}).label || c.change_type;
    return `<tr>
      <td>${esc(fmtDate(c.effective_date))}</td>
      <td>${esc(type)}</td>
      <td>${esc(c.summary || "")}</td>
      <td>${esc(c.reason_category || "—")}${c.reason_notes ? ` <em>(${esc(c.reason_notes)})</em>` : ""}</td>
      <td>${esc(c.approved_by_name || "—")}</td>
      <td>${c.parent_notified ? "Yes" + (c.parent_notified_date ? ` (${esc(fmtDate(c.parent_notified_date))})` : "") : "—"}</td>
      <td>${esc(c.created_by_name || "—")}</td>
    </tr>`;
  }).join("");
  return `
    <div class="print-header">
      <h1>Schedule Change History</h1>
      <div class="subtitle">${esc(studentName)}${yearFilter && yearFilter !== "all" ? ` · ${esc(yearFilter)}` : " · All years"}</div>
    </div>
    <table>
      <thead><tr>
        <th>Effective</th><th>Type</th><th>Summary</th><th>Reason</th><th>Approved by</th><th>Parent notified</th><th>Entered by</th>
      </tr></thead>
      <tbody>${body || '<tr><td colspan="7">No changes</td></tr>'}</tbody>
    </table>`;
}

export function ScheduleChangeHistory({ studentName, changes }) {
  const [expanded, setExpanded] = useState({});
  const [typeFilter, setTypeFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");

  const years = useMemo(() => {
    const set = new Set();
    (changes || []).forEach((c) => { const y = schoolYearOf(c.effective_date); if (y) set.add(y); });
    return Array.from(set).sort().reverse();
  }, [changes]);

  const filtered = useMemo(() => {
    return (changes || []).filter((c) => {
      if (typeFilter !== "all" && c.change_type !== typeFilter) return false;
      if (yearFilter !== "all" && schoolYearOf(c.effective_date) !== yearFilter) return false;
      return true;
    });
  }, [changes, typeFilter, yearFilter]);

  const handlePrint = () => {
    printPage({
      title: `Schedule Change History — ${studentName}`,
      content: buildPrintHtml(studentName, filtered, yearFilter),
    });
  };

  return (
    <Card data-testid="schedule-history-card">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle>Schedule Change History</CardTitle>
            <CardDescription>Every schedule &amp; alternate-plan change, newest first.</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="history-type-filter">
              <option value="all">All types</option>
              <option value="weekly_schedule">Weekly Schedule</option>
              <option value="alt_plan_added">Plan Added</option>
              <option value="alt_plan_updated">Plan Updated</option>
              <option value="alt_plan_ended">Plan Ended</option>
              <option value="initial">Initial</option>
            </select>
            <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="history-year-filter">
              <option value="all">All years</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="history-print-btn">Print / Save PDF</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <p className="text-center py-6 text-muted-foreground" data-testid="history-empty">No schedule changes on record.</p>
        ) : (
          <div className="space-y-2">
            {filtered.map((c) => {
              const typeMeta = CHANGE_LABELS[c.change_type] || { label: c.change_type, cls: "bg-gray-100 text-gray-700" };
              const statusMeta = STATUS_LABELS[c.status] || { label: c.status, cls: "bg-gray-100" };
              const open = !!expanded[c.id];
              return (
                <div key={c.id} className="rounded-lg border" data-testid={`history-entry-${c.id}`}>
                  <button
                    type="button"
                    className="w-full text-left p-3 flex flex-wrap items-start justify-between gap-2 hover:bg-slate-50"
                    onClick={() => setExpanded((p) => ({ ...p, [c.id]: !p[c.id] }))}
                    data-testid={`history-toggle-${c.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{fmtDate(c.effective_date)}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${typeMeta.cls}`}>{typeMeta.label}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusMeta.cls}`}>{statusMeta.label}</span>
                      </div>
                      <p className="text-sm text-gray-700 mt-0.5 truncate">{c.summary}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {c.reason_category || "No reason recorded"}
                        {c.approved_by_name ? ` · Approved by ${c.approved_by_name}` : ""}
                        {" · Parent notified "}{c.parent_notified ? "✓" : "—"}
                        {c.created_by_name ? ` · Entered by ${c.created_by_name}` : ""}
                      </p>
                    </div>
                    <span className="text-gray-400 text-xs shrink-0">{open ? "▲" : "▼"}</span>
                  </button>
                  {open && (
                    <div className="px-3 pb-3 pt-1 border-t bg-slate-50/40">
                      {c.reason_notes && <p className="text-xs text-gray-600 mb-2">Notes: {c.reason_notes}</p>}
                      <ChangeDetail change={c} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
